<template>
  <v-card
    class="mt-4 pa-4"
    border
  >
    <h4 class="mb-1 text-subtitle-2">
      {{ $t('lead-list.filter-settings.labels.status') }}
    </h4>
    <Permission
      v-slot="{ hasPermission }"
      permission="lead:write"
      show
    >
      <v-select
        v-model="selectedStatus"
        :items="statusList"
        :item-props="{ disabled: disabledStatus }"
        item-title="text"
        item-value="value"
        :menu-props="{ maxHeight: 340 }"
        hide-details
        variant="outlined"
        density="compact"
        :disabled="!hasPermission || isMasterView"
        @update:model-value="handleStatusChange"
      />
    </Permission>

    <Feature :feature-slug="featureNames.ASSIGN_LEADS">
      <Permission permission="lead:assign">
        <div>
          <h4 class="mt-4 mb-1 text-subtitle-2">
            {{ $t('lead-list.filter-settings.labels.assigned-employee') }}
          </h4>
          <v-select
            v-model="selectedUserId"
            :items="usersForSelect"
            item-title="text"
            item-value="value"
            placeholder="keiner zugewiesen"
            hide-details
            variant="outlined"
            density="compact"
            clearable
            :disabled="isMasterView"
            @update:model-value="handleAssignedUserChange"
          />
        </div>
      </Permission>
    </Feature>
  </v-card>
</template>

<script>
import Permission from '@/components/Permission'
import bus, { eventNames } from '@/lib/eventBus'
import statusForSelect, { defaultSystemStatuses } from '@/lib/leadStatus'
import { sortByString } from '@/lib/sort-functions'
import feature from '@/mixins/feature'
import GET_LEADS_PIPELINE_STATUSES from '@/modules/leads/LeadList/Cards/queries/GetLeadsPipelineStatuses.gql'
import { Feature } from '@/plugins/feature-config'

import USERS from '../queries/Users.gql'
import ASSIGN_USER from './queries/AssignUser.gql'
import LEAD from './queries/Lead.gql'
import UNASSIGN_USER from './queries/UnassignUser.gql'
import UPDATE_LEAD_STATUS from './queries/UpdateLeadStatus.gql'

export default {
  components: { Permission, Feature },
  mixins: [feature],
  inject: ['role'],
  emits: ['assigned-user-changed'],
  props: {
    status: {
      type: String,
      default: 'new',
    },
    leadId: {
      type: String,
      default: null,
    },
    assignedUserId: {
      type: String,
      default: null,
    },
    isMasterView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedStatus: this.status,
      selectedUserId: this.assignedUserId,
      statusList: [],
    }
  },
  computed: {
    usersForSelect() {
      return (this.users ?? [])
        .map((user) => ({
          value: user.id,
          text: `${user.firstname} ${user.lastname}`,
        }))
        .sort(sortByString('text'))
    },
  },
  watch: {
    status() {
      this.selectedStatus = this.status
    },
  },
  apollo: {
    leadsPipelineStatuses: {
      query: GET_LEADS_PIPELINE_STATUSES,
      variables() {
        return {
          input: {
            isMasterView: false,
            childCompanyId: null,
          },
        }
      },
      update(data) {
        if (!data?.leadsPipelineStatuses) {
          return
        }
        this.statusList = data.leadsPipelineStatuses.map((status) => ({
          value: status.name,
          text: defaultSystemStatuses.includes(status.name)
            ? this.$t(statusForSelect[status.name])
            : status.name,
        }))
      },
    },
    users: {
      query: USERS,
      variables() {
        return {
          input: {
            companyId: this.$auth.user.companyId,
          },
        }
      },
      skip() {
        return !this.role.isAuthorizedFor('user:read')
      },
    },
  },
  methods: {
    disabledStatus({ value }) {
      return this.status !== 'created' && value === 'created'
    },
    async handleStatusChange() {
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_LEAD_STATUS,
          variables: {
            leadId: this.leadId,
            status: this.selectedStatus,
          },
          refetchQueries: [{ query: LEAD, variables: { id: this.leadId } }],
        })
        this.showSuccess(this.$t('alerts.lead.status.success'))
        this.triggerAnimation(this.selectedStatus)
        this.$tracking.event('Lead', 'Selected', 'Status', this.selectedStatus)
      } catch (e) {
        this.selectedStatus = this.status
        this.showError(this.$t('alerts.lead.status.error'))
      }
    },
    async handleAssignedUserChange() {
      try {
        if (this.selectedUserId) {
          await this.assignUser()
        } else {
          await this.unassignUser()
        }
        this.$emit('assigned-user-changed')
        this.showSuccess(this.$t('alerts.lead.status.succes-user'))
        const userName = this.usersForSelect.find((u) => u.value === this.selectedUserId)?.text
        this.$tracking.event('Lead', 'Selected', 'Assigned User', userName || 'none')
      } catch (e) {
        this.selectedUserId = this.assignedUserId
        this.showError(this.$t('alerts.lead.status.error-user'))
      }
    },
    assignUser() {
      return this.$apollo.mutate({
        mutation: ASSIGN_USER,
        variables: {
          leadId: this.leadId,
          userId: this.selectedUserId,
        },
      })
    },
    unassignUser() {
      return this.$apollo.mutate({
        mutation: UNASSIGN_USER,
        variables: {
          leadId: this.leadId,
        },
      })
    },
    showSuccess(message) {
      bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'success', text: message })
    },
    showError(message) {
      bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'error', text: message })
    },
    triggerAnimation(selectedStatus) {
      const showAnimationStates = ['reached', 'orderReceived']
      if (showAnimationStates.includes(selectedStatus)) {
        bus.$emit(eventNames.TRIGGER_ANIMATION)
      }
    },
  },
}
</script>
